import React, { useState, useCallback } from 'react';
import {
  X,
  Upload,
  FileText,
  AlertCircle,
  FileType,
  File,
  Presentation,
  ClipboardPaste,
  Files,
} from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@aaas/shared';
import { Button } from '@aaas/shared';
import { Alert, AlertDescription } from '@aaas/shared';
import { Progress } from '@aaas/shared';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@aaas/shared';
import { Textarea } from '@aaas/shared';
import { Input } from '@aaas/shared';

interface DocumentUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (files: File[]) => Promise<void>;
  onPaste?: (content: string, title?: string) => Promise<void>;
  spaceName: string;
}

const DocumentUploadModal: React.FC<DocumentUploadModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  onPaste,
  spaceName,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [pastedContent, setPastedContent] = useState('');
  const [pastedTitle, setPastedTitle] = useState('');
  const [activeTab, setActiveTab] = useState<'upload' | 'paste'>('upload');

  const validateFile = (file: File): boolean => {
    // Support all document types that backend can process
    const validTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX
      'application/msword', // DOC
      'application/vnd.openxmlformats-officedocument.presentationml.presentation', // PPTX
      'application/vnd.ms-powerpoint', // PPT
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // XLSX
      'application/vnd.ms-excel', // XLS
      'text/plain', // TXT
      'text/markdown', // MD
      'text/x-markdown', // Alternative MD mime type
      'text/html', // HTML
    ];
    const validExtensions = [
      '.pdf',
      '.docx',
      '.doc',
      '.pptx',
      '.ppt',
      '.xlsx',
      '.xls',
      '.txt',
      '.md',
      '.markdown',
      '.html',
      '.htm',
    ];
    const maxSize = 100 * 1024 * 1024; // 100MB limit (matching backend)

    const fileName = file.name.toLowerCase();
    const hasValidExtension = validExtensions.some((ext) => fileName.endsWith(ext));

    // Check by extension first (more reliable), then by MIME type
    if (!hasValidExtension) {
      // If extension doesn't match, check MIME type as fallback
      if (!validTypes.includes(file.type)) {
        const supportedFormats = validExtensions.join(', ');
        setError(`Invalid file type: ${file.name}. Supported formats: ${supportedFormats}`);
        return false;
      }
    }

    if (file.size > maxSize) {
      setError(`File too large: ${file.name}. Maximum size is 100MB.`);
      return false;
    }

    return true;
  };

  const validateFileCount = (newFiles: File[]): boolean => {
    const MAX_FILES = 20;
    const totalFiles = files.length + newFiles.length;

    if (totalFiles > MAX_FILES) {
      setError(`Maximum ${MAX_FILES} files per upload. You selected ${totalFiles} files total.`);
      return false;
    }

    return true;
  };

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      setError(null);

      const droppedFiles = Array.from(e.dataTransfer.files);

      if (!validateFileCount(droppedFiles)) {
        return;
      }

      const validFiles = droppedFiles.filter(validateFile);

      if (validFiles.length > 0) {
        setFiles((prevFiles) => [...prevFiles, ...validFiles]);
      }
    },
    [files.length]
  );

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);

      if (!validateFileCount(selectedFiles)) {
        return;
      }

      const validFiles = selectedFiles.filter(validateFile);

      if (validFiles.length > 0) {
        setFiles((prevFiles) => [...prevFiles, ...validFiles]);
      }
    }
  };

  const removeFile = (index: number) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (activeTab === 'upload') {
      if (files.length === 0) {
        setError('Please select at least one document to upload.');
        return;
      }

      setIsUploading(true);
      setError(null);
      setUploadProgress(0);

      try {
        // Simulate progress (in real implementation, track actual upload progress)
        const progressInterval = setInterval(() => {
          setUploadProgress((prev) => Math.min(prev + 10, 90));
        }, 200);

        await onUpload(files);

        clearInterval(progressInterval);
        setUploadProgress(100);

        // Close modal after successful upload
        setTimeout(() => {
          onClose();
          setFiles([]);
          setUploadProgress(0);
        }, 500);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to upload documents');
      } finally {
        setIsUploading(false);
      }
    } else {
      // Handle paste content
      if (!pastedContent.trim()) {
        setError('Please enter some text content to save as a document.');
        return;
      }

      if (!onPaste) {
        setError('Paste functionality is not available.');
        return;
      }

      setIsUploading(true);
      setError(null);
      setUploadProgress(0);

      try {
        // Simulate progress
        const progressInterval = setInterval(() => {
          setUploadProgress((prev) => Math.min(prev + 10, 90));
        }, 200);

        await onPaste(pastedContent, pastedTitle || undefined);

        clearInterval(progressInterval);
        setUploadProgress(100);

        // Close modal after successful creation
        setTimeout(() => {
          onClose();
          setPastedContent('');
          setPastedTitle('');
          setUploadProgress(0);
          setActiveTab('upload');
        }, 500);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create document from text');
      } finally {
        setIsUploading(false);
      }
    }
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.toLowerCase().split('.').pop();

    switch (extension) {
      case 'pdf':
        return <FileType className="h-4 w-4 text-red-500" />;
      case 'docx':
      case 'doc':
        return <FileText className="h-4 w-4 text-blue-500" />;
      case 'pptx':
      case 'ppt':
        return <Presentation className="h-4 w-4 text-orange-500" />;
      case 'xlsx':
      case 'xls':
        return <FileText className="h-4 w-4 text-green-600" />;
      case 'txt':
        return <File className="h-4 w-4 text-gray-500" />;
      case 'md':
      case 'markdown':
        return <FileText className="h-4 w-4 text-green-500" />;
      case 'html':
      case 'htm':
        return <FileText className="h-4 w-4 text-purple-500" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Add Documents to "{spaceName}"</DialogTitle>
          <DialogDescription>
            Upload documents or paste text content. Supported formats: PDF, Word (DOCX), Excel
            (XLSX), PowerPoint (PPTX), Text, Markdown, and HTML. Content will be processed, chunked,
            and indexed for semantic search.
          </DialogDescription>
        </DialogHeader>

        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as 'upload' | 'paste')}
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="upload" className="flex items-center gap-2">
              <Files className="h-4 w-4" />
              Upload Files
            </TabsTrigger>
            <TabsTrigger value="paste" className="flex items-center gap-2">
              <ClipboardPaste className="h-4 w-4" />
              Paste Content
            </TabsTrigger>
          </TabsList>

          {error && (
            <Alert variant="destructive" className="mt-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <TabsContent value="upload" className="space-y-4">
            {/* Drop zone */}
            <div
              className={`
              border-2 border-dashed rounded-lg p-8 text-center transition-colors
              ${isDragging ? 'border-primary bg-primary/5' : 'border-gray-300'}
              ${isUploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() =>
                !isUploading && document.getElementById('document-file-input')?.click()
              }
            >
              <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-lg font-medium mb-2">Drop documents here or click to browse</p>
              <p className="text-sm text-gray-500">
                Supports PDF, DOCX, XLSX, PPTX, TXT, Markdown, and HTML files up to 100MB each
              </p>
              <p className="text-xs text-gray-400 mt-1">Maximum 20 files per upload</p>
              <input
                id="document-file-input"
                type="file"
                className="hidden"
                multiple
                accept=".pdf,.docx,.doc,.pptx,.ppt,.xlsx,.xls,.txt,.md,.markdown,.html,.htm"
                onChange={handleFileInput}
                disabled={isUploading}
              />
            </div>

            {/* File list */}
            {files.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Selected files ({files.length})</h4>
                <div className="max-h-[200px] overflow-y-auto space-y-2">
                  {files.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-gray-50 rounded-md"
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {getFileIcon(file.name)}
                        <span className="text-sm truncate">{file.name}</span>
                        <span className="text-xs text-gray-500">
                          ({(file.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </div>
                      {!isUploading && (
                        <button
                          onClick={() => removeFile(index)}
                          className="p-1 hover:bg-gray-200 rounded"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="paste" className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="title" className="text-sm font-medium">
                Document Title (Optional)
              </label>
              <Input
                id="title"
                type="text"
                placeholder="Enter a title for this document..."
                value={pastedTitle}
                onChange={(e) => setPastedTitle(e.target.value)}
                disabled={isUploading}
              />
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <label htmlFor="content" className="text-sm font-medium">
                  Text Content
                </label>
                {pastedContent && (
                  <span className="text-xs text-gray-500">
                    {pastedContent.length} characters •{' '}
                    {pastedContent.split(/\s+/).filter(Boolean).length} words
                  </span>
                )}
              </div>
              <Textarea
                id="content"
                placeholder="Paste your text content here..."
                value={pastedContent}
                onChange={(e) => setPastedContent(e.target.value)}
                disabled={isUploading}
                className="min-h-[300px] font-mono text-sm"
              />
              <p className="text-xs text-gray-500">
                The text will be saved as a .txt file and processed like other documents
              </p>
            </div>
          </TabsContent>

          {/* Upload progress */}
          {isUploading && (
            <div className="space-y-2 mt-4">
              <div className="flex justify-between text-sm">
                <span>
                  {activeTab === 'upload'
                    ? `Uploading ${files.length} file(s)...`
                    : 'Creating document from text...'}
                </span>
                <span>{uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} className="h-2" />
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={onClose} disabled={isUploading}>
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={
                isUploading || (activeTab === 'upload' ? files.length === 0 : !pastedContent.trim())
              }
            >
              {isUploading
                ? activeTab === 'upload'
                  ? 'Uploading...'
                  : 'Creating...'
                : activeTab === 'upload'
                  ? `Upload ${files.length} Document(s)`
                  : 'Create Document'}
            </Button>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentUploadModal;
